import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type ResponseStatus = 'accepted' | 'already_accepted' | 'expired' | 'not_found' | 'error';

interface AcceptOfferResponse {
  success: boolean;
  status: ResponseStatus;
  message: string;
  booking?: {
    id: string;
    confirmation_number: string;
    business_name: string;
    service_name: string;
    booking_date: string;
    booking_time: string;
  };
}

export default function OfferAcceptancePage() {
  const { token } = useParams<{ token: string }>();
  const [loading, setLoading] = useState(true);
  const [response, setResponse] = useState<AcceptOfferResponse | null>(null);

  useEffect(() => {
    const acceptOffer = async () => {
      if (!token) {
        setResponse({
          success: false,
          status: 'error',
          message: 'Invalid link'
        });
        setLoading(false);
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke('accept-waitlist-offer', {
          body: { acceptance_token: token }
        });

        if (error) {
          console.error('Error calling accept-waitlist-offer:', error);
          setResponse({
            success: false,
            status: 'error',
            message: 'Failed to process your request. Please try again or contact support.'
          });
        } else {
          setResponse(data);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setResponse({
          success: false,
          status: 'error',
          message: 'An unexpected error occurred. Please contact support.'
        });
      } finally {
        setLoading(false);
      }
    };

    acceptOffer();
  }, [token]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border border-white/10">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
              <p className="text-gray-300">Processing your appointment...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (!response) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <XCircle className="h-6 w-6 text-red-500" />
              <CardTitle className="text-white">Error</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300">Failed to load response. Please try again.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success states (accepted or already_accepted)
  if (response.status === 'accepted' || response.status === 'already_accepted') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <CheckCircle className="h-6 w-6 text-green-500" />
              <CardTitle className="text-white">
                {response.status === 'accepted' ? 'Appointment Confirmed!' : 'Already Confirmed'}
              </CardTitle>
            </div>
            <CardDescription className="text-gray-400">
              {response.message}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {response.booking && (
              <>
                <div className="bg-white/5 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm text-gray-400">Confirmation Number</p>
                    <p className="text-lg font-semibold text-white">{response.booking.confirmation_number}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Business</p>
                    <p className="text-white">{response.booking.business_name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-400">Service</p>
                    <p className="text-white">{response.booking.service_name}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-400">Date</p>
                      <p className="text-white">{new Date(response.booking.booking_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-400">Time</p>
                      <p className="text-white">{response.booking.booking_time}</p>
                    </div>
                  </div>
                </div>
                <p className="text-sm text-gray-400 text-center">
                  You will receive a confirmation email shortly.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired state
  if (response.status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <Clock className="h-6 w-6 text-orange-500" />
              <CardTitle className="text-white">Offer Expired</CardTitle>
            </div>
            <CardDescription className="text-gray-400">
              {response.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 text-sm">
              This appointment offer has expired. If you're still interested in booking, please contact the business directly or join the waitlist again.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Not found state
  if (response.status === 'not_found') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border border-white/10">
          <CardHeader>
            <div className="flex items-center space-x-2">
              <AlertCircle className="h-6 w-6 text-yellow-500" />
              <CardTitle className="text-white">Offer Not Found</CardTitle>
            </div>
            <CardDescription className="text-gray-400">
              {response.message}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <p className="text-gray-300 text-sm">
              We couldn't find this appointment offer. The link may be invalid or the offer may have been withdrawn.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Generic error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-black/40 backdrop-blur-xl border border-white/10">
        <CardHeader>
          <div className="flex items-center space-x-2">
            <XCircle className="h-6 w-6 text-red-500" />
            <CardTitle className="text-white">Error</CardTitle>
          </div>
          <CardDescription className="text-gray-400">
            {response.message}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-gray-300 text-sm">
            If this problem persists, please contact support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
